import React, { FC, Fragment, MouseEvent, useEffect, useState } from 'react';
import { Button, Divider, Popover, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import { isEqual } from 'lodash';

import {
  GroupContentApprovalType,
  PublicUserProfileFragment,
  UserFeedFollowGroupFragment,
} from 'api';
import { useReleases } from 'hooks';

import Branded from './Filter/Branded';
import Source from './Filter/Source';
import Commentary from './Filter/Commentary';
import CTAType from './Filter/CTAType';
import Groups from './Filter/Groups';
import Submitters from './Filter/Submitters';
import Submitter from './Filter/SubmitterRole';
import Moderation from './Filter/Moderation';
import { SearchContentFilter } from 'features/admin/types';
import { CheckedMode, UseCheckedState } from 'types/app';

interface Props extends Partial<UseCheckedState> {
  filter: SearchContentFilter;
  setFilter: (filter: SearchContentFilter) => void;
  clearFilter: () => void;
  selectedUsers: PublicUserProfileFragment[];
  setSelectedUsers: (profiles: PublicUserProfileFragment[]) => void;
  selectedGroups: UserFeedFollowGroupFragment[];
  setSelectedGroups: (groups: UserFeedFollowGroupFragment[]) => void;
}

const styles = {
  button: {
    height: '100%',
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
} as const;

const Filters: FC<Props> = ({
  filter,
  setFilter,
  clearFilter,
  selectedUsers,
  setSelectedUsers,
  selectedGroups,
  setSelectedGroups,
  ...rest
}) => {
  const { setCheckedState } = rest;
  const { t } = useTranslation();
  const releases = useReleases();

  const has4EyeModeration = releases.includes('4EyeModeration');

  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilter, setTempFilter] = useState<SearchContentFilter>(filter);

  useEffect(() => {
    if (!isEqual(filter, tempFilter)) {
      setTempFilter(filter);
    }
  }, [filter]);

  function handleReconciliation() {
    setTempFilter(filter);
  }

  function onClickButton(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function onClose() {
    setAnchorEl(null);
  }

  function onSubmit() {
    if (has4EyeModeration && tempFilter.moderation === GroupContentApprovalType.FourEyes) {
      setCheckedState(CheckedMode.OFF);
    }
    setFilter({ ...tempFilter });
    onClose();
  }

  function renderButtons() {
    return (
      <Grid xs={12} container justifyContent="flex-end" spacing={2}>
        <Grid>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              clearFilter();
              onClose();
            }}
          >
            {t('common:clear')}
          </Button>
        </Grid>

        <Grid>
          <Button variant="contained" color="primary" onClick={onSubmit}>
            {t('common:apply')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      <Button
        variant="contained"
        color="secondary"
        size="large"
        onClick={onClickButton}
        endIcon={<AddCircleRoundedIcon color="primary" />}
        sx={{ ...styles.button, ...(filter.isExternalShares && styles.disabled) }}
      >
        {t('analytics:filters')}
      </Button>

      <Popover
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        TransitionProps={{
          onExited: handleReconciliation,
          onEnter: handleReconciliation,
        }}
      >
        <Grid container sx={{ width: 600 }} paddingX={4} paddingY={4}>
          <Groups
            filter={tempFilter}
            setFilter={setTempFilter}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
          />

          <Submitters
            filter={tempFilter}
            setFilter={setTempFilter}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
          />

          <Submitter filter={tempFilter} setFilter={setTempFilter} />

          <CTAType filter={tempFilter} setFilter={setTempFilter} />

          {has4EyeModeration && <Moderation filter={tempFilter} setFilter={setTempFilter} />}

          <Branded filter={tempFilter} setFilter={setTempFilter} />

          <Source filter={tempFilter} setFilter={setTempFilter} />

          <Commentary filter={tempFilter} setFilter={setTempFilter} />

          <Grid xs={12} marginBottom={1}>
            <Divider />
          </Grid>

          {renderButtons()}
        </Grid>
      </Popover>
    </Fragment>
  );
};

export default Filters;
